import { useMemo } from 'react'
import { Card } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import type { Eticheta, IngredientItem, BeneficiuItem, LabelStandard } from './types'
import { NIVEL_DOTS, autoritateCorecta } from './types'
import { valideazaEticheta } from './validation'

interface Props {
  draft: Partial<Eticheta>
  standards: LabelStandard[]
  onExportTxt: () => void
  exportLoading: boolean
  onBackToGenerator: () => void
  isPersisted: boolean
}

export function PreviewTab({
  draft,
  standards,
  onExportTxt,
  exportLoading,
  onBackToGenerator,
  isPersisted,
}: Props) {
  const result = useMemo(() => valideazaEticheta(draft), [draft])
  const avertizari = standards
    .filter((s) => s.tip === 'avertizare')
    .sort((a, b) => a.sortOrder - b.sortOrder)
  const vnr = standards.find((s) => s.tip === 'vnr')?.valoare
  const marca = standards.find((s) => s.tip === 'marca')?.valoare
  const website = standards.find((s) => s.tip === 'website')?.valoare

  if (!draft.denumire) {
    return (
      <Card>
        <div className="rounded border border-dashed border-b4l-border p-8 text-center text-sm text-b4l-text-muted">
          Nicio etichetă în lucru. Mergi la tabul Generator și începe o etichetă nouă.
          <div className="mt-3">
            <Button variant="secondary" onClick={onBackToGenerator}>
              ← La Generator
            </Button>
          </div>
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <Card title="Validare Standard B4L">
        {result.issues.length === 0 && result.warnings.length === 0 ? (
          <div className="rounded bg-b4l-green-light p-3 text-sm text-b4l-green-dark">
            ✓ Toate verificările au trecut. Eticheta e gata de aprobare.
          </div>
        ) : (
          <>
            {result.issues.length > 0 && (
              <div className="mb-2 rounded border border-b4l-red-dark/30 bg-b4l-red-light p-3 text-xs">
                <div className="mb-1 font-semibold text-b4l-red-dark">
                  ✗ Probleme blocante ({result.issues.length})
                </div>
                <ul className="ml-4 list-disc space-y-0.5 text-b4l-red-dark">
                  {result.issues.map((i, idx) => (
                    <li key={idx}>{i}</li>
                  ))}
                </ul>
              </div>
            )}
            {result.warnings.length > 0 && (
              <div className="rounded border border-b4l-amber-dark/30 bg-b4l-amber-light p-3 text-xs">
                <div className="mb-1 font-semibold text-b4l-amber-dark">
                  ! Avertizări ({result.warnings.length})
                </div>
                <ul className="ml-4 list-disc space-y-0.5 text-b4l-amber-dark">
                  {result.warnings.map((w, idx) => (
                    <li key={idx}>{w}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </Card>

      <Card>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {/* Sectiunea stanga */}
          <div className="space-y-2 text-xs">
            <div className="text-[10px] uppercase tracking-wide text-b4l-text-muted">Stânga</div>
            <h2 className="text-base font-bold uppercase">PREMIUM {draft.denumire}</h2>
            {(draft.ingrediente as IngredientItem[])?.map((i) => (
              <div key={i.key} className="rounded border border-b4l-border p-1.5">
                <div className="flex items-center gap-1.5">
                  <span className={`inline-block h-2 w-2 rounded-full ${NIVEL_DOTS[i.nivel]}`} />
                  <span className="font-semibold">{i.label}</span>
                  {i.doza && <span className="text-b4l-text-muted">{i.doza}</span>}
                </div>
                {i.mentiune && <div className="mt-1 text-[11px] italic">{i.mentiune}</div>}
              </div>
            ))}
            {(draft.beneficii as BeneficiuItem[])?.length > 0 && (
              <div className="mt-2">
                <div className="font-semibold">Beneficii:</div>
                <ul className="space-y-0.5">
                  {(draft.beneficii as BeneficiuItem[]).map((b, i) => (
                    <li key={i} className="flex items-start gap-1">
                      <span
                        className={`mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full ${
                          b.status === 'verde'
                            ? 'bg-b4l-green'
                            : b.status === 'portocaliu'
                              ? 'bg-b4l-amber'
                              : b.status === 'rosu'
                                ? 'bg-b4l-red'
                                : 'bg-b4l-text-muted'
                        }`}
                      />
                      <span>{b.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {draft.gramaj && (
              <div className="text-[10px] text-b4l-text-muted">
                Cantitate netă: {draft.gramaj} e
              </div>
            )}
            {(draft.pictograme ?? []).length > 0 && (
              <div className="flex flex-wrap gap-1">
                {(draft.pictograme ?? []).map((p) => (
                  <span
                    key={p}
                    className="rounded bg-b4l-bg px-1.5 py-0.5 text-[9px] font-medium text-b4l-text-dim"
                  >
                    {p.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Sectiunea centrala */}
          <div className="rounded bg-b4l-blue-light/30 p-3 text-center text-xs">
            <div className="text-[10px] uppercase tracking-wide text-b4l-text-muted">Centru</div>
            <div className="mt-2 text-2xl font-bold text-b4l-blue-dark">B4L</div>
            <div className="mt-1 font-semibold uppercase">{draft.denumire}</div>
            {draft.subtitlu && <div className="text-[11px]">{draft.subtitlu}</div>}
            {draft.portii && <div className="mt-1 font-bold">{draft.portii}</div>}
            {draft.madeIn && <div className="mt-2 text-[11px] font-medium">{draft.madeIn}</div>}
            {website && <div className="mt-1 text-[11px] text-b4l-blue-dark">{website}</div>}
          </div>

          {/* Sectiunea dreapta */}
          <div className="space-y-2 text-xs">
            <div className="text-[10px] uppercase tracking-wide text-b4l-text-muted">Dreapta</div>
            <div>
              <div className="font-semibold">AVERTIZĂRI</div>
              <ul className="space-y-0.5 text-[10px]">
                {avertizari.map((a) => (
                  <li key={a.id}>• {a.valoare}</li>
                ))}
                {draft.avertSpecific && <li className="font-medium">• {draft.avertSpecific}</li>}
              </ul>
            </div>
            {draft.compozitie && (
              <div>
                <div className="font-semibold">Ingrediente per porție</div>
                <pre className="whitespace-pre-wrap font-mono text-[9px]">{draft.compozitie}</pre>
              </div>
            )}
            {vnr && <div className="text-[9px] italic text-b4l-text-muted">{vnr}</div>}
            {(draft.utilizareAdulti || draft.utilizareCopii) && (
              <div>
                <div className="font-semibold">Mod utilizare</div>
                {draft.utilizareAdulti && (
                  <div className="text-[10px]">Adulți: {draft.utilizareAdulti}</div>
                )}
                {draft.utilizareCopii && (
                  <div className="text-[10px]">Copii: {draft.utilizareCopii}</div>
                )}
              </div>
            )}
            {draft.ean && <div className="font-mono text-[10px]">EAN: {draft.ean}</div>}
          </div>
        </div>

        <div className="mt-4 border-t border-b4l-border pt-3 text-[11px] text-b4l-text-muted">
          {draft.tara && <div>{draft.tara}</div>}
          {draft.adresa && <div>{draft.adresa}</div>}
          {draft.autoritate && draft.notificare && (
            <div>
              {autoritateCorecta(draft.autoritate)}: {draft.notificare}
            </div>
          )}
          {draft.detinator && <div>Deținător: {draft.detinator}</div>}
          {marca && <div className="mt-1 italic">{marca}</div>}
        </div>
      </Card>

      <Card>
        <div className="flex items-center justify-between gap-3">
          <Button variant="secondary" onClick={onBackToGenerator}>
            ← Înapoi la Generator
          </Button>
          <div className="flex gap-2">
            <Button
              onClick={onExportTxt}
              disabled={!isPersisted || result.issues.length > 0}
              loading={exportLoading}
              title={
                !isPersisted
                  ? 'Salvează întâi eticheta'
                  : result.issues.length > 0
                    ? 'Rezolvă problemele blocante'
                    : ''
              }
            >
              Export TXT
            </Button>
          </div>
        </div>
        {!isPersisted && (
          <p className="mt-2 text-[11px] text-b4l-text-muted">
            Salvează eticheta din Generator (butonul {'„Generează eticheta”'}) înainte să exporți.
          </p>
        )}
      </Card>
    </div>
  )
}
